import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Loader from '../../modules/layout/components/Loader';
import AppBreadcrumb from '../../modules/layout/components/AppBreadcrumb';
import FiltersCard from '../../modules/layout/components/FiltersCard';
import Pagination from '../../modules/layout/components/Pagination';
import { useToast } from '../../modules/layout/components/ToastContext';
import { getAll, post } from '../_request';
import { Formik, Form, Field } from 'formik';
import SortDirection from '../../modules/layout/components/SortDirection';
import { IProfileItemDto } from '../../interfaces/IUtilities';

type ClaimChange = {
  ClaimType: string;
  OldValue: string;
  NewValue: string;
};

type EventItem = {
  Id: string;
  Action: string;
  ProfileId: string;
  AdminProfileId: string;
  AdminUsername: string;
  ClientIp: string;
  ServerName: string;
  Timestamp: string;
  ClaimChange: ClaimChange | null;
};

export default function ProfileEventsHistoryMongodb() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentProfileId, setCurrentProfileId] = useState('');
  const [searchResults, setSearchResults] = useState<IProfileItemDto[]>([]);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [sortField, setSortField] = useState<'username' | 'fullname'>('username');
  const [asc, setAsc] = useState(true);
  const [timestampAsc, setTimestampAsc] = useState(false);

  const fetchEvents = async (profileId: string, p: number, ps: number, tsAsc?: boolean) => {
    if (!profileId) {
      showToast && showToast(t('ProfileIdRequired') ?? 'Profile ID is required', 'warning');
      return;
    }

    setLoading(true);
    setShowUserPicker(false);
    setSearchResults([]);

    try {
      const order = tsAsc ?? timestampAsc ? 'asc' : 'desc';
      const result: any = await getAll(
        `/api/UserEvents/getmongoProfileevents/${encodeURIComponent(profileId)}`,
        `?page=${p}&pageSize=${ps}&order=${order}`
      );
      const respStatus = result?.response?.status || result?.status;

      if (respStatus === 200 || respStatus === undefined) {
        const payload = result?.data?.Data ?? result?.data ?? result;
        setEvents(Array.isArray(payload?.Items) ? payload.Items : []);
        setTotal(payload?.TotalCount ?? 0);
        setHasSearched(true);

        if (!payload?.Items?.length && showToast)
          showToast(t('NoDataFound'), 'info');
      } else if (respStatus === 403) navigate('/error/403');
      else if (respStatus === 401) navigate('/auth/login');
      else if (respStatus === 500) navigate('/error/500');
      else showToast && showToast(t('StatusCode', { code: String(respStatus) }), 'error');
    } catch (e: any) {
      showToast && showToast(t('ConnectionError', { error: String(e) }), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTimestampSort = () => {
    const newAsc = !timestampAsc;
    setTimestampAsc(newAsc);
    setPage(1);
    fetchEvents(currentProfileId, 1, pageSize, newAsc);
  };

  const handleSubmit = async (values: any) => {
    let profileId = values.ProfileId?.trim();

    if (profileId) {
      setCurrentProfileId(profileId);
      setPage(1);
      await fetchEvents(profileId, 1, pageSize);
      return;
    }

    const hasAnyFilter = Boolean(
      values.Username || values.FirstName || values.LastName || values.Fullname || values.Email
    );

    if (!hasAnyFilter) {
      showToast && showToast(t('SelectAtLeastOneFilter'), 'warning');
      return;
    }

    setLoading(true);
    try {
      const searchResult: any = await post('/api/UserEvents/findprofiles', {
        Username: values.Username,
        FirstName: values.FirstName,
        LastName: values.LastName,
        Fullname: values.Fullname,
        Email: values.Email,
        Exact: false,
        First: 0,
        Max: 100
      });

      const payload = searchResult?.data?.Data ?? searchResult?.data ?? searchResult;
      const items = Array.isArray(payload?.Items) ? payload.Items
        : Array.isArray(payload) ? payload : [];

      if (!items || items.length === 0) {
        showToast && showToast(t('NoDataFound'), 'info');
        return;
      }

      if (items.length === 1) {
        const pid = items[0].ProfileId ?? '';
        setCurrentProfileId(pid);
        setPage(1);
        await fetchEvents(pid, 1, pageSize);
      } else {
        setSearchResults(items);
        setShowUserPicker(true);
      }
    } catch (e: any) {
      showToast && showToast(t('ConnectionError', { error: String(e) }), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <AppBreadcrumb
        items={[{ title: t('Home'), href: '/' }, { title: t('ProfileEventsHistory') }]}
      />
      <h3 className="mb-3">{t('ProfileEventsHistory')}</h3>

      <FiltersCard isActive={true}>
        <Formik
          initialValues={{
            Username: '', FirstName: '', LastName: '', Fullname: '', Email: '', ProfileId: ''
          }}
          onSubmit={handleSubmit}
        >
          {({ resetForm }) => (
            <Form>
              <div className="mb-3 row g-2 align-items-end">
                <div className="col-md-3">
                  <label className="form-label">{t('Username')}</label>
                  <Field name="Username" className="form-control" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('FirstName')}</label>
                  <Field name="FirstName" className="form-control" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('LastName')}</label>
                  <Field name="LastName" className="form-control" />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('FullName')}</label>
                  <Field name="Fullname" className="form-control" />
                </div>
                <div className="col-md-3 mt-2">
                  <label className="form-label">{t('Email')}</label>
                  <Field name="Email" className="form-control" />
                </div>
                <div className="col-md-3 mt-2">
                  <label className="form-label">{t('ProfileId')}</label>
                  <Field name="ProfileId" className="form-control" />
                </div>
                <div className="col-md-3 mt-2 d-flex gap-2 align-items-end">
                  <button type="submit" className="btn gg-btn gg-btn-submit">{t('Search')}</button>
                  <button type="button" className="btn btn-secondary text-white"
                    onClick={() => {
                      resetForm();
                      setEvents([]);
                      setHasSearched(false);
                      setCurrentProfileId('');
                      setSearchResults([]);
                      setShowUserPicker(false);
                    }}>
                    {t('Reset')}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </FiltersCard>

      {loading && <Loader />}

      {showUserPicker && !loading && (
        <div className="mt-3">
          <div className="alert alert-info">{t('MultipleProfilesFound') ?? 'Multiple profiles found. Select one to view their events.'}</div>
          <div className="table-responsive">
            <table className="table table-striped table-sm table-hover">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer' }} onClick={() => {
                    if (sortField === 'username') setAsc(!asc); else { setSortField('username'); setAsc(true); }
                  }}>
                    {t('Username')} <SortDirection isActive={sortField === 'username'} order={asc ? 'asc' : 'desc'} />
                  </th>
                  <th style={{ cursor: 'pointer' }} onClick={() => {
                    if (sortField === 'fullname') setAsc(!asc); else { setSortField('fullname'); setAsc(true); }
                  }}>
                    {t('FullName')} <SortDirection isActive={sortField === 'fullname'} order={asc ? 'asc' : 'desc'} />
                  </th>
                  <th>{t('Email')}</th>
                  <th>{t('ProfileState')}</th>
                </tr>
              </thead>
              <tbody className="table-group-divider">
                {searchResults
                  .slice()
                  .sort((a, b) => {
                    const getVal = (p: IProfileItemDto) =>
                      sortField === 'username' ? (p.Username ?? '') : (p.Name ?? p.Fullname ?? '');
                    const cmp = getVal(a).toLowerCase().localeCompare(getVal(b).toLowerCase());
                    return asc ? cmp : -cmp;
                  })
                  .map((p, idx) => (
                    <tr key={p.ProfileId ?? idx}
                      className="clickable-row text-primary"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        const pid = p.ProfileId ?? '';
                        setCurrentProfileId(pid);
                        setPage(1);
                        fetchEvents(pid, 1, pageSize);
                      }}>
                      <td>{p.Username}</td>
                      <td>{p.Name ?? p.Fullname ?? ''}</td>
                      <td>{p.Email}</td>
                      <td>{p.ProfileState ?? ''}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!hasSearched && !loading && !showUserPicker && (
        <div className="alert alert-info mt-3">
          {t('SetFiltersAndSearch') ?? 'Please set filters above and click Search to load results.'}
        </div>
      )}

      {hasSearched && !loading && !showUserPicker && (
        <div className="table-responsive">
          <table className="table table-striped table-sm">
            <colgroup>
              <col style={{ width: '220px' }} />
              <col style={{ width: '170px' }} />
              <col style={{ width: '130px' }} />
              <col style={{ width: '150px' }} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>{t('Action')}</th>
                <th style={{ cursor: 'pointer' }} onClick={handleTimestampSort}>
                  {t('Timestamp')} <SortDirection isActive={true} order={timestampAsc ? 'asc' : 'desc'} />
                </th>
                <th>{t('IpAddress')}</th>
                <th>{t('ChangedBy')}</th>
                <th>{t('Changes')}</th>
              </tr>
            </thead>
            <tbody className="table-group-divider">
              {events.length === 0 && (
                <tr><td colSpan={5} className="text-center">{t('NoDataFound')}</td></tr>
              )}
              {events.map((event, i) => (
                <tr key={event.Id ?? i}>
                  <td>{event.Action}</td>
                  <td>{event.Timestamp ? new Date(event.Timestamp).toLocaleString() : ''}</td>
                  <td>{event.ClientIp}</td>
                  <td>{event.AdminUsername || event.AdminProfileId}</td>
                  <td>
                    {event.ClaimChange && (
                      <div className="profile-events">
                        <ul className="mb-0 changes-list">
                          <li className="mb-1">
                            <div className="changes-row">
                              <div className="changes-prop">{event.ClaimChange.ClaimType}:</div>
                                <div className="change-badge old" title={event.ClaimChange.OldValue ?? '—'}>
                                    <span className="change-label">{t('OldValue')}:</span>
                                    <span className="change-value">{event.ClaimChange.OldValue ?? '—'}</span>
                                </div>
                                <div className="change-badge new" title={event.ClaimChange.NewValue ?? '—'}>
                                    <span className="change-label">{t('NewValue')}:</span>
                                    <span className="change-value">{event.ClaimChange.NewValue ?? '—'}</span>
                                </div>
                            </div>
                          </li>
                        </ul>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            currentPage={page}
            onPageChange={(p) => { setPage(p); fetchEvents(currentProfileId, p, pageSize); }}
            totalItems={total}
            pageSize={pageSize}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); fetchEvents(currentProfileId, 1, s); }}
          />
        </div>
      )}
    </div>
  );
}